import { useTranslation } from './common/useTranslation.js';
import { createContext, useContext, useState, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import PresentationGateSelection from './common/PresentationGateSelection';

import { apiFetch } from '../services/api/client';
import { catalog, applyPresentation, getPresentation } from './runtime';
import { createPreferences } from './preferences';
import { Users, Shield, X } from 'lucide-react';
import './common/selection.css';

const Context = createContext(null);
const safeStorage = {
  getItem: key => { try { return localStorage.getItem(key); } catch { return null; } },
  setItem: (key, value) => { try { localStorage.setItem(key, value); } catch { /* In-memory session works. */ } },
};
function sessionFromStorage() {
  try {
    const token = safeStorage.getItem('goodvale_token');
    const user = JSON.parse(safeStorage.getItem('goodvale_user') || 'null');
    return token && user?.userId ? { id: user.userId, token } : null;
  } catch { return null; }
}
export function usePresentation() {
  const value = useContext(Context);
  if (!value) throw new Error('PresentationProvider is missing');
  return value;
}

export function PresentationSelector({ cards = false }) {
  const { t, i18n } = useTranslation();
  const { preference, openSelection, selectionRequested } = usePresentation();
  const language = i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'de';
  return <fieldset className={`presentation-selector ${cards ? 'presentation-cards' : 'presentation-switch'}`}>
    <legend>{t('presentation.label')}</legend>
    <div className="presentation-options" data-count={catalog.available.length}>
      {catalog.available.map(option => {
        const isSelected = preference === option.id;
        const optionName = option.labels[language] || option.labels.en;
        const imgSrc = catalog.package(option.id).preview;
        return (
          <button key={option.id} type="button" className={`presentation-option presentation-option-${option.id} ${isSelected ? 'is-selected' : ''}`} onClick={openSelection} aria-pressed={isSelected} aria-haspopup="dialog" aria-expanded={selectionRequested}>
            {cards && (
              <div className="presentation-card-visual">
                <img src={imgSrc} alt="" />
              </div>
            )}
            <span className="presentation-option-name">{optionName}</span>
            {cards && <span className="presentation-option-description">{t(`presentation.${option.id}Description`, { defaultValue: option.labels[language] || option.id })}</span>}
          </button>
        );
      })}
    </div>
  </fieldset>;
}

export function PresentationModeSwitch({ className = '' }) {
  const { t, i18n } = useTranslation();
  const { mode, openSelection, selectionRequested } = usePresentation();
  const language = i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'de';
  const option = catalog.available.find(option => option.id === mode);
  const modeName = t(`presentation.gateNames.${mode}`, { defaultValue: option?.labels[language] || option?.labels.en || mode });
  const Icon = mode === 'community' ? Users : Shield;
  return (
    <button
      type="button"
      onClick={openSelection}
      aria-haspopup="dialog"
      aria-expanded={selectionRequested}
      aria-label={`${t('presentation.gameMode')}: ${modeName}`}
      title={`${t('presentation.choose')} (${modeName})`}
      className={`presentation-mode-switch world-appearance-toggle inline-flex items-center justify-center p-2 rounded-full border transition-colors ${className}`}
    >
      <Icon className="w-4 h-4" aria-hidden="true" />
    </button>
  );
}

function PresentationGate() {
  const { t } = useTranslation();
  const { loading, needsChoice, loadError, retry, selectionRequested, closeSelection } = usePresentation();
  const location = useLocation();
  const { pathname } = location;
  const exempt = ['/login', '/legal', '/app/legal', '/forgot-password', '/reset-password', '/confirm-email'].includes(pathname);
  const open = selectionRequested || (!exempt && (loading || needsChoice));
  const canClose = selectionRequested && !loading && !needsChoice;
  const ref = useRef(null);
  useEffect(() => {
    if (open && !ref.current.open) { ref.current.showModal(); ref.current.focus(); }
    if (!open && ref.current.open) ref.current.close();
  }, [open]);
  // Modal interactions must not dismiss the menu that opened the selection.
  return <dialog ref={ref} className="presentation-gate" tabIndex={-1} aria-label={t('presentation.gameMode')} onPointerDown={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()} onCancel={event => { event.preventDefault(); if (canClose) closeSelection(); }}>
    {loading || loadError ? <div className="presentation-gate-loading">
      <p role="status">{t(loadError ? 'presentation.loadError' : 'presentation.loading')}</p>
      {loadError && <button type="button" className="world-button" onClick={retry}>{t('presentation.reload')}</button>}
    </div> : open && <PresentationGateSelection />}
    {canClose && <button type="button" className="presentation-gate-close" onClick={closeSelection} aria-label={t('common.close')}><X size={20} aria-hidden="true" /></button>}
  </dialog>;
}
export function PresentationProvider({ children }) {
  const [selectionRequested, setSelectionRequested] = useState(false);
  const [controller] = useState(() => createPreferences({
    catalog, storage: safeStorage, onMode: applyPresentation,
    save: async (presentationMode, owner) => {
      await apiFetch('/users/profile', { method: 'PUT', headers: { Authorization: `Bearer ${owner.token}` }, body: JSON.stringify({ presentationMode }) });
      const current = sessionFromStorage();
      if (current?.id === owner.id) {
        const user = JSON.parse(safeStorage.getItem('goodvale_user') || '{}');
        safeStorage.setItem('goodvale_user', JSON.stringify({ ...user, presentationMode }));
      }
    },
  }));
  const state = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const { t } = useTranslation();
  useLayoutEffect(() => {
    let owner;
    const sync = (force = false) => {
      const next = sessionFromStorage();
      const signature = next ? `${next.id}:${next.token}` : 'guest';
      if (!force && signature === owner) return;
      owner = signature;
      controller.connect(next, session => apiFetch('/auth/me', { headers: { Authorization: `Bearer ${session.token}` } }));
    };
    sync();
    const auth = () => sync();
    const storage = event => {
      if (event.key?.startsWith('goodvale_presentation_')) sync(true);
      else sync();
    };
    const focus = () => { if (!controller.getSnapshot().pending) sync(true); };
    window.addEventListener('goodvale-auth-change', auth);
    window.addEventListener('storage', storage);
    window.addEventListener('focus', focus);
    return () => { window.removeEventListener('goodvale-auth-change', auth); window.removeEventListener('storage', storage); window.removeEventListener('focus', focus); };
  }, [controller]);
  return <Context.Provider value={{ ...state, resources: getPresentation(), selectionRequested, openSelection: () => setSelectionRequested(true), closeSelection: () => setSelectionRequested(false), choose: id => { const saved = controller.choose(id); setSelectionRequested(false); return saved; }, retry: () => controller.retry() }}>
    <div className="presentation-app" data-presentation-loading={state.loading || undefined}>{children}</div>
    <PresentationGate />
    {(state.error || state.unsupported || state.pending) && <div className="presentation-status" role="status">
      {state.error ? <>{t(state.loadError ? 'presentation.loadError' : 'presentation.error')} <button type="button" onClick={() => controller.retry()}>{t(state.loadError ? 'presentation.reload' : 'presentation.retry')}</button></> : t(state.unsupported ? 'presentation.unsupported' : 'presentation.saving')}
    </div>}
  </Context.Provider>;
}
